import {
  Alert,
  Box,
  Button,
  ButtonDropdown,
  Modal,
  SpaceBetween,
} from '@cloudscape-design/components';
import { doc, getFirestore, updateDoc } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import PropTypes from 'prop-types';
import React, { useState } from 'react';
import ReactDOM from 'react-dom';

import { usePartAndQuote } from '../../features/firebase';
import { useUserData } from '../../providers/userDataProvider';

function QuoteStatus({ partId }) {
  const { part, partUser, quote } = usePartAndQuote(partId);
  const { uid } = part;
  const {
    status, hasPendingWrites, id: quoteID, feedbackChecklistReviewed,
  } = quote;
  const { user: { allowLiveChanges } } = useUserData();

  const [isSetQuotedOpen, setIsSetQuotedOpen] = useState(false);
  const [isSetNoQuoteOpen, setIsSetNoQuoteOpen] = useState(false);
  const [isRevokeQuoteOpen, setIsRevokeQuoteOpen] = useState(false);
  const [emailError, setEmailError] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const sendEmail = async (state) => {
    const functions = getFunctions();
    const sendQuoteEmail = httpsCallable(functions, 'sendQuoteEmailV2');
    await sendQuoteEmail({ userID: uid, type: state });
  };

  const handleChangeState = async (state) => {
    await updateDoc(doc(getFirestore(), 'quotes', quoteID), { status: state });
  };

  if (hasPendingWrites || !status) {
    return <Button disabled>Loading...</Button>;
  }

  if (status === 'pending') {
    return (
      <>
        {typeof document === 'undefined'
          ? null
          : ReactDOM.createPortal(
            <Modal
              visible={isSetQuotedOpen}
              footer={(
                <Box float="right">
                  <SpaceBetween size="xs" direction="horizontal">
                    <Button variant="primary" onClick={() => setIsSetQuotedOpen(false)}>
                      Cancel
                    </Button>
                    {allowLiveChanges && (
                      <Button
                        onClick={async () => {
                          await handleChangeState('quoted');
                          setIsSetQuotedOpen(false);
                        }}
                      >
                        Confirm without email
                      </Button>
                    )}
                    <Button
                      disabled={!allowLiveChanges || isSubmitting}
                      onClick={async () => {
                        setIsSubmitting(true);
                        try {
                          setEmailError(null);
                          await sendEmail('quoted');
                          await handleChangeState('quoted');
                          setIsSetQuotedOpen(false);
                        } catch (err) {
                          setEmailError(err.message);
                        }
                        setIsSubmitting(false);
                      }}
                    >
                      Confirm and send email
                    </Button>
                  </SpaceBetween>
                </Box>
              )}
              onDismiss={() => { setIsSetQuotedOpen(false); }}
              header="Mark quoted"
            >
              { emailError ? (
                <Alert
                  statusIconAriaLabel="Error"
                  type="error"
                  header="Error sending email."
                >
                  {emailError}
                </Alert>
              ) : null}
              {allowLiveChanges ? (
                <p>
                  Are you sure you want to set this part as
                  {' '}
                  <span style={{ fontWeight: 800 }}>QUOTED</span>
                  ?
                  Once you click &quot;Confirm,&quot; the quote will be live.
                </p>
              ) : (
                <p>Please ask Andy, Josh, Hao, Scott, or Duncan to set this quote live.</p>
              )}
            </Modal>,
            document.body,
          )}
        {typeof document === 'undefined'
          ? null
          : ReactDOM.createPortal(
            <Modal
              visible={isSetNoQuoteOpen}
              onDismiss={() => { setIsSetNoQuoteOpen(false); }}
              header="Mark No Quote?"
              footer={(
                <Box float="right">
                  <SpaceBetween size="xs" direction="horizontal">
                    <Button variant="primary" onClick={() => { setIsSetNoQuoteOpen(false); }}>
                      Cancel
                    </Button>
                    {allowLiveChanges && (
                      <Button
                        onClick={async () => {
                          await handleChangeState('no_quote');
                          setIsSetNoQuoteOpen(false);
                        }}
                      >
                        Confirm without email
                      </Button>
                    )}
                    <Button
                      disabled={!allowLiveChanges || isSubmitting}
                      onClick={async () => {
                        setIsSubmitting(true);
                        try {
                          setEmailError(null);
                          await sendEmail('no_quote');
                          await handleChangeState('no_quote');
                          setIsSetNoQuoteOpen(false);
                        } catch (err) {
                          setEmailError(err.message);
                        }
                        setIsSubmitting(false);
                      }}
                    >
                      Confirm and send email
                    </Button>
                  </SpaceBetween>
                </Box>
              )}
            >
              { emailError ? (
                <Alert
                  statusIconAriaLabel="Error"
                  type="error"
                  header="Error sending email."
                >
                  {emailError}
                </Alert>
              ) : null}
              {allowLiveChanges ? (
                <p>
                  Are you sure you want to set this part as
                  {' '}
                  <span style={{ fontWeight: 800 }}>NO QUOTE</span>
                  ? Once you click &quot;Confirm,&quot; the quote will be live.
                </p>
              ) : (
                <p>Please ask Andy, Josh, Hao, Scott, or Duncan to set this quote live.</p>
              )}
            </Modal>,
            document.body,
          )}
        <ButtonDropdown
          items={[
            {
              text: 'Set quote live',
              id: 'quoted',
              disabled: !feedbackChecklistReviewed || !partUser.organization,
              disabledReason: `${!partUser.organization ? 'Please select the user organization to proceed.' : 'Please review the part and provide feedback to proceed.'}`,
            },
            {
              text: 'Mark "no quote"',
              id: 'no_quote',
              disabled: !feedbackChecklistReviewed || !partUser.organization,
              disabledReason: `${!partUser.organization ? 'Please select the user organization to proceed.' : 'Please review the part and provide feedback to proceed.'}`,
            },
          ]}
          onItemClick={({ detail }) => {
            switch (detail.id) {
              case 'quoted':
                setIsSetQuotedOpen(true);
                break;
              case 'no_quote':
                setIsSetNoQuoteOpen(true);
                break;
              default:
            }
          }}
        >
          Set quote live
        </ButtonDropdown>
      </>
    );
  }
  return (
    <>
      {typeof document === 'undefined'
        ? null
        : ReactDOM.createPortal(
          <Modal
            visible={isRevokeQuoteOpen}
            onDismiss={() => { setIsRevokeQuoteOpen(false); }}
            header="Revoke quote?"
            footer={(
              <Box float="right">
                <SpaceBetween size="xs" direction="horizontal">
                  <Button variant="primary" onClick={() => { setIsRevokeQuoteOpen(false); }}>
                    Cancel
                  </Button>
                  <Button onClick={() => { handleChangeState('pending'); setIsRevokeQuoteOpen(false); }}>
                    Confirm
                  </Button>
                </SpaceBetween>
              </Box>
            )}
          >
            <p>
              Are you sure you want to
              {' '}
              <span style={{ fontWeight: 800 }}>REVOKE</span>
              {' '}
              this quote?
            </p>
          </Modal>,
          document.body,
        )}
      <Button
        variant="primary"
        onClick={() => { setIsRevokeQuoteOpen(true); }}
      >
        Revoke quote
      </Button>
    </>
  );
}

QuoteStatus.propTypes = {
  partId: PropTypes.string.isRequired,
};

export default QuoteStatus;
